import { db } from './db';

// Shared data-access layer used both by the "online" path (mirroring server
// writes for cache consistency) and the "offline" path (writing the record
// that will later be replayed from the sync queue). All functions here are
// intentionally generic over storeName so callers in createApi.js don't
// duplicate this logic per entity type.

export async function putLocal(storeName, record, syncStatus = 'pending') {
    return db.table(storeName).put({ ...record, _syncStatus: syncStatus });
}

export async function getAllLocal(storeName, indexQuery) {
    const table = db.table(storeName);
    if (!indexQuery) return table.toArray();
    const [[key, value]] = Object.entries(indexQuery);
    return table.where(key).equals(value).toArray();
}

export async function markSynced(storeName, id) {
    return db.table(storeName).update(id, { _syncStatus: 'synced' });
}

// `kind: 'photo'` items carry a Blob (`blob`/`fileName`) instead of a JSON
// `body`, and are replayed as a multipart upload by the sync engine — see
// syncEngine.js. `uploadedImageUrl` starts null and is filled in once the
// upload step succeeds, so a retry of the follow-up step never re-uploads.
export async function enqueue({
    entityType, entityId, method, url, body, dependsOnEntityId = null,
    kind = 'json', blob = null, fileName = null,
}) {
    return db.syncQueue.add({
        entityType,
        entityId,
        method,
        url,
        body,
        dependsOnEntityId,
        kind,
        blob,
        fileName,
        uploadedImageUrl: null,
        status: 'pending',
        attempts: 0,
        lastError: null,
        lastAttemptAt: null,
        createdAt: Date.now(),
    });
}

export async function getQueue() {
    return db.syncQueue.orderBy('seq').toArray();
}

export async function updateQueueItem(seq, changes) {
    return db.syncQueue.update(seq, changes);
}

export async function dequeue(seq) {
    return db.syncQueue.delete(seq);
}

export async function markQueueItemError(seq, errorMessage) {
    return db.syncQueue.update(seq, { status: 'error', lastError: errorMessage, lastAttemptAt: Date.now() });
}

// Defensive: if the app was closed/reloaded mid-drain, any 'in_flight' rows
// left over from a previous session must become retryable again, otherwise
// they'd sit forever without being picked up by the next drain pass.
export async function resetInFlightToPending() {
    const stuck = await db.syncQueue.where('status').equals('in_flight').toArray();
    await Promise.all(stuck.map((row) => db.syncQueue.update(row.seq, { status: 'pending' })));
}

export async function getCachedMeta(key) {
    const row = await db.meta.get(key);
    return row ? row.value : undefined;
}

export async function setCachedMeta(key, value) {
    return db.meta.put({ key, value });
}
